import nutritionData from '../../../data/nutrition/nutrition.json';
import calorieData from '../../../data/nutrition/calorie.json';

/**
 * カロリー・栄養素・PFC比の計算機クラス
 * 
 * @class Calculator
 * @classdesc
 * 指定された要素に基づき、栄養素計算・消費カロリー計算・PFC比計算の各種フォームを初期化し、
 * 入力値に応じた結果を表示する。年齢・性別・活動レベル等の選択肢制御や、アニメーション展開も行う。
 * 
 * @param {HTMLElement} el - 計算機を初期化する対象要素
 * 
 * @property {HTMLElement} el - 計算機のルート要素
 * @property {string} type - 計算機のタイプ（nutrition, calorie, pfc）
 * @property {HTMLElement} nutritionAllTable - 栄養素一覧テーブル要素
 * @property {HTMLElement} calorieAllTable - カロリー一覧テーブル要素
 * @property {HTMLElement} resultEl - 計算結果表示要素
 * 
 * @method init - 計算機タイプに応じた初期化処理を実行
 * @method _setupActivityOptions - 年齢に応じて活動レベルの選択肢を制御
 * @method initNutrition - 栄養素計算フォームの初期化
 * @method renderNutritionTable - 選択された年齢・性別の栄養素テーブルを描画
 * @method renderNutritionListAll - 全年齢・性別の栄養素一覧テーブルを描画
 * @method initCalorie - 消費カロリー計算フォームの初期化
 * @method renderCalorieListAll - 全年齢・性別のカロリー一覧テーブルを描画
 * @method calculateCalorie - 入力値に基づき消費カロリーを計算
 * @method initPfc - PFC比計算フォームの初期化
 * @method _mapAgeGroup - 年齢ラベルをデータキーに変換
 * @method _mapCalorieAge - 年齢ラベルをカロリーデータ用キーに変換
 * @method _animateExpand - 結果表示エリアの展開アニメーション
 */
/**
 * 各種計算機能（栄養素・カロリー・PFC比）を提供するクラス
 */
export default class Calculator {
  /**
   * Calculatorクラスのインスタンスを生成します。
   * @param {HTMLElement} el - 計算機のルート要素
   */
  constructor(el, lenisInstance = null) {
    if (!el) return;
    this.el = el;
    this.type = el.dataset.type;
    this.lenis = lenisInstance ?? window.lenis ?? null;
    if (!this.type) return;

    this.init();
  }
  

  /**
   * 計算機の種類に応じて初期化処理を実行します。
   */
  init() {
    switch (this.type) {
      case 'nutrition':
        this.initNutrition();
        break;
      case 'calorie':
        this.initCalorie();
        break;
      case 'pfc':
        this.initPfc();
        break;
      default:
        return;
    }

      const submitBtn = this.el.querySelector('button[type="submit"]');
      if (submitBtn) submitBtn.disabled = false;
  }

  /**
   * 年齢に応じて活動レベルの選択肢を制御します。
   * @private
   * @param {HTMLFormElement} form - 対象のフォーム要素
   */
  _setupActivityOptions(form) {
    const ageSelect = form.querySelector('select[name="age"]');
    const activitySelect = form.querySelector('select[name="activity"]');

    const adjust = () => {
      const age = ageSelect.value;
      Array.from(activitySelect.options).forEach(opt => {
        if (['1〜2歳', '3〜5歳'].includes(age)) {
          opt.disabled = opt.value !== 'ふつう';
        } else if (age === '75歳以上') {
          opt.disabled = opt.value === '高い';
        } else {
          opt.disabled = false;
        }
      });
      // 選択中のoptionが無効化された場合は「ふつう」に戻す
      if (activitySelect.selectedOptions[0].disabled) {
        activitySelect.value = 'ふつう';
      }
    };

    // 初期化時と年齢変更時に選択肢を調整
    adjust();
    ageSelect.addEventListener('change', adjust);
  }

  // ───────── 栄養素計算 ─────────

  /**
   * 栄養素計算機の初期化処理を行います。
   */
  initNutrition() {
    this.nutritionAllTable = document.getElementById('js-nutrition-all-table');
    this.renderNutritionListAll();

    const form = this.el.querySelector('form');
    this.resultEl = this.el.querySelector('.o-calculator__result');
    this.inputBody = this.el.querySelector('.o-calculator__body');

    // 年齢・性別選択時のテーブル表示処理
    form.addEventListener('submit', e => {
      e.preventDefault();
      const data = new FormData(form);
      const gender = data.get('gender'); // 'male' or 'female'
      const ageKey = this._mapAgeGroup(data.get('age')); // '18-29' など
      if (!ageKey) {
        this.resultEl.textContent = '年齢グループが正しく選択されていません';
        return;
      }
      this.renderNutritionTable(gender, ageKey);
      this._animateExpand(this.resultEl, {
        scrollTarget: this.inputBody
      });
    });
  }

  /**
   * 選択された年齢・性別に応じた栄養素テーブルを描画します。
   * @param {string} gender - 性別（'male' または 'female'）
   * @param {string} ageKey - 年齢グループキー（例: '18-29'）
   */
renderNutritionTable(gender, ageKey) {
  const formatNutrient = (name) =>
    name
      .replace('ビタミンA', 'ビタミンA<sup>※2</sup>')
      .replace('（参考）推定エネルギー必要量', '（参考）<br>推定エネルギー<br>必要量')
      .replace(/（.*?）/g, m => `<small>${m}</small>`);

  const formatNutrientTd = (name) =>
    name
    .replace(/月経あり\d+(\.\d+)?mg/g, m => `<small>${m}</small>`)
  .replace(/月経なし\d+(\.\d+)?mg/g, m => `<small>${m}</small>`);

  // 与えられた items（slice 後の配列）を baseName ごとにグループ化
  const groupByBase = items => items.reduce((acc, item) => {
    const baseName = item.nutrient.replace(/（.*?）/g, '');
    if (!acc[baseName]) acc[baseName] = [];
    acc[baseName].push(item);
    return acc;
  }, {});

  // グループごとに <tr> を作成
  const createRows = items => {
    const groups = groupByBase(items);
    return Object.values(groups).map(groupItems => {
      // 代表アイテム（栄養素名やクラス判定用）
      const first = groupItems[0];

      // 栄養素セル
      const nutrientHtml = `<th${first.nutrient === '（参考）推定エネルギー必要量' ? ' class="is-break"' : ''}>${formatNutrient(first.nutrient)}</th>`;

      // 参照値セル （もしグループ内で reference が異なるなら改行で結合）
      const refs = [...new Set(
        groupItems.map(i => i.reference)
      )];
      const referenceHtml = refs.length > 1
        ? `<td>${refs.join('<br>')}</td>`
        : (first.reference === '（参考）推定エネルギー必要量'
            ? `<td class="is-break">（参考）<br>推定エネルギー<br>必要量</td>`
            : `<td>${first.reference}</td>`);

      // 値セル：年齢・性別ごとの値を取り出し、重複を排除して改行で結合
      const vals = [...new Set(
        groupItems.map(i => i.ageGroups[ageKey]?.[gender] ?? '-')
      )];
      const valHtml = `<td>${formatNutrientTd(vals.join(''))}</td>`;

      return `<tr>${nutrientHtml}${referenceHtml}${valHtml}</tr>`;
    }).join('');
  };

  // 前半・後半に分けてテーブル生成
  const firstPart  = createRows(nutritionData.slice(0, 17));
  const secondPart = createRows(nutritionData.slice(17));

  this.resultEl.innerHTML = `
    <table class="o-calculator__table o-calculator__table--nutrition">
      <tbody>${firstPart}</tbody>
    </table>
    <table class="o-calculator__table o-calculator__table--nutrition">
      <tbody>${secondPart}</tbody>
    </table>
  `;
}

  /**
   * 全年齢・性別の栄養素一覧テーブルを描画します。
   */
  renderNutritionListAll() {
    const ageKeys = Object.keys(nutritionData[0].ageGroups);

    // テーブルヘッダー作成
    const header1 = [
      `<th rowspan="2" colspan="2">身体活動レベル「ふつう」の場合</th>`,
      ...ageKeys.map(k => `<th colspan="2">${k.replace('-', '～')}歳</th>`)
    ].join('');
    const header2 = ageKeys.map(() => `<th>男性</th><th>女性</th>`).join('');

    // 栄養素名のフォーマット
    const formatNutrient = (name) =>
      name
        .replace('ビタミンA', 'ビタミンA<sup>※2</sup>')
        .replace(/（.*?）/g, m => `<small>${m}</small>`);

    const formatNutrientTd = (name) =>
      name
        .replace('（参考）推定エネルギー必要量', '（参考）<br>推定エネルギー<br>必要量')

    // データをグループ化
    const grouped = {};
    for (const item of nutritionData) {
      const baseName = item.nutrient.replace(/（.*?）/g, '');
      if (!grouped[baseName]) grouped[baseName] = [];
      grouped[baseName].push(item);
    }

    // 各グループを1行にまとめて出力
    const rows = Object.entries(grouped).map(([_, group]) => {
      const displayName = group[0].nutrient;
      const nutrientTh = `<th>${formatNutrient(displayName)}</th>`;

      // 参照値を一意化し、brで結合
      const references = [...new Set(group.map(item => item.reference))];
      const isBreakRef = references.length > 1 || (references.length === 1 && references[0] === '（参考）推定エネルギー必要量');
      const refClass = isBreakRef ? ' class="is-break"' : '';
      const refHtml = `<td${refClass}>${formatNutrientTd(references.join('<br>'))}</td>`;

      // 年齢・性別ごとの値を一意化し、brで結合
      const valueCells = ageKeys.flatMap(k => {
        const maleVals = [...new Set(group.map(item => item.ageGroups[k]?.male ?? '-'))];
        const femaleVals = [...new Set(group.map(item => item.ageGroups[k]?.female ?? '-'))];
        const maleClass = maleVals.length > 1 ? ' class="is-break"' : '';
        const femaleClass = femaleVals.length > 1 ? ' class="is-break"' : '';
        const maleHtml = `<td${maleClass}>${maleVals.join('<br>')}</td>`;
        const femaleHtml = `<td${femaleClass}>${femaleVals.join('<br>')}</td>`;
        return [maleHtml, femaleHtml];
      });

      // 複数アイテムまとめ行には is-unit クラス
      const trClass = group.length > 1 ? ' class="is-unit"' : '';
      return `<tr${trClass}>${nutrientTh}${refHtml}${valueCells.join('')}</tr>`;
    }).join('');

    // テーブル描画
    this.nutritionAllTable.innerHTML = `
      <table class="o-calculator__table">
        <thead>
          <tr>${header1}</tr>
          <tr>${header2}</tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  /**
   * 全年齢・性別のカロリー一覧テーブルを描画します。
   */
  renderCalorieListAll() {
    const level = 'ふつう';
    const maleData = calorieData.male[level];
    const femaleData = calorieData.female[level];

    // Backup: 妊娠・授乳婦の付加カロリーの描画を除外
    // const excludeLabels = ['妊娠(付加量)初期', '妊娠(付加量)中期', '妊娠(付加量)後期', '授乳婦(付加量)'];
    // const ageLabels = Object.keys(maleData).filter(age => !excludeLabels.includes(age));

    // const tableRows = ageLabels.map(age => {
    const tableRows = Object.keys(maleData).map(age => {
      const maleVal = maleData[age] ?? '-';
      const femaleVal = femaleData[age] ?? '-';
      return `<tr><th>${age}</th><td>${maleVal}kcal</td><td>${femaleVal}kcal</td></tr>`;
    }).join('');

    const pregnancyRows = `
      <tr><th>妊娠(付加量)初期</th><td>―</td><td>50kcal</td></tr>
      <tr><th>妊娠(付加量)中期</th><td>―</td><td>250kcal</td></tr>
      <tr><th>妊娠(付加量)後期</th><td>―</td><td>450kcal</td></tr>
      <tr><th>授乳婦(付加量)</th><td>―</td><td>350kcal</td></tr>
    `;

    this.calorieAllTable.innerHTML = `
      <table class="o-calculator__table o-calculator__table--calorie">
        <thead>
          <tr><th></th><th>男性</th><th>女性</th></tr>
        </thead>
        <tbody>
          ${tableRows}
          ${pregnancyRows}
        </tbody>
      </table>
    `;
  }

  // ───────── 消費カロリー計算 ─────────

  /**
   * 消費カロリー計算機の初期化処理を行います。
   */
  initCalorie() {
    this.calorieAllTable = document.getElementById('js-calorie-all-table');
    this.renderCalorieListAll();

    const form = this.el.querySelector('form');
    this.resultEl = this.el.querySelector('.o-calculator__result');
    this.inputBody = this.el.querySelector('.o-calculator__body');

    this._setupActivityOptions(form);

    // フォーム送信時のカロリー計算・表示
    form.addEventListener('submit', e => {
      e.preventDefault();
      const data = new FormData(form);
      const kcal = this.calculateCalorie(data);

      if (kcal == null) {
        this.resultEl.textContent = '該当データがありません';
      } else {
        this.resultEl.innerHTML = `
          <p class="o-calculator__result-txt">
            あなたの1日に必要な摂取カロリーは… 
            <strong class="txt_border">${Number(kcal).toLocaleString()} kcal</strong>
          </p>`;
      }

      // 結果表示部を展開
      this._animateExpand(this.resultEl, {
        scrollTarget: this.inputBody
      });
    });
  }

  /**
   * 入力データから該当するカロリー値を取得します。
   * @param {FormData} data - フォームデータ
   * @returns {number|null} カロリー値（該当データがない場合はnull）
   */

  calculateCalorie(data) {
    const gender = data.get('gender');       // "male","female","female-pregnant-early",…
    const ageKey = this._mapCalorieAge(data.get('age'));
    const level  = data.get('activity');

    //「女性-妊婦/授乳婦」は"female"で計算
    const baseGender = gender.startsWith('female-') ? 'female' : gender;

    // カロリー取得
    const rawBase = calorieData[baseGender]?.[level]?.[ageKey];
    const baseKcal = rawBase != null ? Number(rawBase) : null;
    if (baseKcal == null) return null;


    // 付加カロリー定義
    const additionalMap = {
      'female-pregnant-early': 50,
      'female-pregnant-mid':   250,
      'female-pregnant-late':  450,
      'female-lactating':      350
    };

    // 妊娠・授乳婦の場合は追加カロリーを加算
    const additional = additionalMap[gender] ?? 0;
    return baseKcal + additional;
  }

  // ───────── PFC比計算 ─────────

  /**
   * PFC比計算機の初期化処理を行います。
   */
  initPfc() {
    const form = this.el.querySelector('form');
    this.resultEl = this.el.querySelector('.o-calculator__result');
    this.inputBody = this.el.querySelector('.o-calculator__body');
    this._setupActivityOptions(form);

    // フォーム送信時のPFC比計算・表示
    form.addEventListener('submit', e => {
      e.preventDefault();
      const data = new FormData(form);
      let kcal = this.calculateCalorie(data);
      if (kcal == null) {
        this.resultEl.textContent = '先に消費カロリーを正しく計算してください';
        return;
      }
      // 各PFC比率を取得（未入力時はデフォルト値を使用）
      const pRatio = 0.15;
      const fRatio = 0.25;
      const cRatio = 0.6;
      const pGrams = Math.round((kcal * pRatio) / 4);
      const fGrams = Math.round((kcal * fRatio) / 9);
      const cGrams = Math.round((kcal * cRatio) / 4);
      
      const pRatioKcal = Math.round(kcal * pRatio);
      const fRatioKcal = Math.round(kcal * fRatio);
      const cRatioKcal = Math.round(kcal * cRatio);
      // 結果表示
      this.resultEl.innerHTML = '';
      this.resultEl.innerHTML += `
        <p class="o-calculator__result-txt">あなたの1日に必要な摂取カロリーは... <strong class="txt_border">${Number(kcal).toLocaleString()} kcal</strong></p>
      `;
      this.resultEl.innerHTML += `
        <div class="o-pfc-result">
          <div class="o-pfc-result__head">
            <h3 class="txt_border">1日の摂るべきPFC</h3>
          </div>
          <div class="o-pfc-result__body">
            <dl>
              <dt>タンパク質（P）</dt>
              <dd>
                <span class="o-pfc-result__value">${pGrams}</span>
                <span class="o-pfc-result__unit">g</span>
              </dd>
              <dd>
                <span class="o-pfc-result__value">${pRatioKcal}</span>
                <span class="o-pfc-result__unit">kcal</span>
              </dd>
            </dl>
            <dl>
              <dt>脂質（F）</dt>
              <dd>
                <span class="o-pfc-result__value">${fGrams}</span>
                <span class="o-pfc-result__unit">g</span>
              </dd>
              <dd>
                <span class="o-pfc-result__value">${fRatioKcal}</span>
                <span class="o-pfc-result__unit">kcal</span>
              </dd>
            </dl>
            <dl>
              <dt>炭水化物（C）</dt>
              <dd>
                <span class="o-pfc-result__value">${cGrams}</span>
                <span class="o-pfc-result__unit">g</span>
              </dd>
              <dd>
                <span class="o-pfc-result__value">${cRatioKcal}</span>
                <span class="o-pfc-result__unit">kcal</span>
              </dd>
            </dl>
          </div>
        </div>
      `;

      this._animateExpand(this.resultEl, {
        scrollTarget: this.inputBody
      });
    });
  }

  /**
   * 年齢ラベルを年齢グループキーに変換します。
   * @private
   * @param {string} label - 年齢ラベル（例: "18～29歳"）
   * @returns {string} 年齢グループキー（例: "18-29"）
   */
  _mapAgeGroup(label) {
    return label?.replace('～', '-').replace('歳', '').trim();
  }

  /**
   * 年齢ラベルをカロリーデータ用のキーに変換します。
   * @private
   * @param {string} label - 年齢ラベル
   * @returns {string} カロリーデータ用キー
   */
  _mapCalorieAge(label) {
    return label
      ?.replace('～', '～')
      .replace('歳', '（歳）')
      .replace('75（歳）以上', '75以上（歳）')
      .trim();
  }

  // ───────── アニメーション共通関数 ─────────

  /**
   * 結果表示エリアをアニメーションで展開します。
   * @private
   * @param {HTMLElement} el - 展開対象の要素
   * @param {Object} [options] - オプション
   * @param {HTMLElement} [options.scrollTarget] - スクロール対象
   * @param {string} [options.scrollBehavior] - スクロール動作
   */
  _animateExpand(el, options = {}) {
  if (!el) return;
  const { scrollTarget = el, scrollBehavior = 'smooth' } = options;
  el.classList.add('is-visible');
  const isFirst = !el.dataset.expanded;

  const header = document.querySelector('.l-header');
  // ヘッダー分のマイナス + 10px
  let offset = -(header?.offsetHeight || 0);
  offset += 10;

  if(this.type === 'calorie' || this.type === 'pfc') {
    if (!window.matchMedia('(min-width: 768px)').matches) {
      // document.documentElement.clientWidth はビューポート幅
      const extra = document.documentElement.clientWidth * 0.355;
      offset += extra;
    }
  }

  const scrollOpts = {
    offset,
    duration: 0.5,
    easing: t => 1 - Math.pow(1 - t, 3),
  };

  const lenis = this.lenis;

  if (isFirst) {
    el.style.transition = '';
    el.style.maxHeight = '0px';
    void el.offsetHeight; // リフロー
    el.style.maxHeight = el.scrollHeight + 'px';

    const onEnd = e => {
      if (e.propertyName !== 'max-height') return;
      el.removeEventListener('transitionend', onEnd);
      el.style.maxHeight = 'none';
      el.dataset.expanded = 'true';
      if (lenis?.scrollTo) {
        lenis.scrollTo(scrollTarget, scrollOpts);
        lenis.resize?.();
        lenis.start?.();
      }
    };
    el.addEventListener('transitionend', onEnd);

  } else {
    // 2回目以降はスクロールのみ
    if (lenis?.scrollTo) {
      lenis.scrollTo(scrollTarget, scrollOpts);
    }
  }
}

}
